import { useEffect, useState, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useToast } from "../hooks/useToast";
import {
  listAll, createStorefront, updateStorefront,
  deleteStorefront, setPublishState, getProducts,
} from "../utils/api";
import StorefrontDrawer from "../components/StorefrontDrawer";

export default function AdminPage() {
  const { isAdmin } = useAuth();
  const toast = useToast();

  const [storefronts, setStorefronts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [drawerSf, setDrawerSf] = useState(null); // null = closed, {} = new, {...} = edit
  const [busy, setBusy]         = useState({});    // id → true while a request is in-flight
  const [previewProds, setPreviewProds] = useState({}); // id → product count

  if (!isAdmin) return <Navigate to="/admin/login" replace />;

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listAll();
      setStorefronts(data);
    } catch (e) {
      toast(e.message, "error");
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { reload(); }, [reload]);

  const setBusyFor = (id, val) => setBusy(b => ({ ...b, [id]: val }));

  // ── Create / update ───────────────────────────────────────
  const handleSave = async (form) => {
    if (form.id) {
      const updated = await updateStorefront(form.id, form);
      setStorefronts(s => s.map(x => x.id === updated.id ? updated : x));
      toast("Saved", "success");
    } else {
      const created = await createStorefront(form);
      setStorefronts(s => [created, ...s]);
      toast("Storefront created", "success");
    }
  };

  // ── Publish / unpublish ───────────────────────────────────
  const handlePublish = async (sf) => {
    const action = sf.publish_state === "published" ? "unpublish" : "publish";
    setBusyFor(sf.id, true);
    try {
      const result = await setPublishState(sf.id, action);
      setStorefronts(s => s.map(x => x.id === sf.id
        ? { ...x, publish_state: result.publish_state, published_at: result.published_at }
        : x
      ));
      if (action === "publish") {
        toast(`Published → /s/${sf.slug}`, "success");
      } else {
        toast("Moved back to draft", "info");
      }
    } catch (e) {
      toast(e.message, "error");
    } finally { setBusyFor(sf.id, false); }
  };

  // ── Delete ────────────────────────────────────────────────
  const handleDelete = async (sf) => {
    if (!confirm(`Delete "${sf.title}"? This cannot be undone.`)) return;
    setBusyFor(sf.id, true);
    try {
      await deleteStorefront(sf.id);
      setStorefronts(s => s.filter(x => x.id !== sf.id));
      toast("Deleted", "success");
    } catch (e) {
      toast(e.message, "error");
    } finally { setBusyFor(sf.id, false); }
  };

  // ── Cache bust / product count preview ────────────────────
  const handlePreview = async (sf) => {
    setBusyFor(sf.id + "_preview", true);
    try {
      const { products, fromCache, stale } = await getProducts(sf.id, { bust: true, isAdmin: true });
      setPreviewProds(p => ({ ...p, [sf.id]: products.length }));
      toast(
        `${products.length} products fetched${fromCache ? " (cached)" : " fresh"}${stale ? " ⚠ stale" : ""}`,
        stale ? "error" : "success"
      );
    } catch (e) {
      toast(`PA-API: ${e.message}`, "error");
    } finally { setBusyFor(sf.id + "_preview", false); }
  };

  return (
    <div className="container main-area">
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                   padding:"32px 0 24px",flexWrap:"wrap",gap:12}}>
        <div>
          <h1 style={{fontFamily:"var(--ff-d)",fontSize:"2rem",fontWeight:700}}>Storefronts</h1>
          <p style={{color:"var(--muted)",fontSize:".9rem",marginTop:4}}>
            {storefronts.length} storefront{storefronts.length!==1?"s":""} ·{" "}
            {storefronts.filter(s=>s.publish_state==="published").length} published
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setDrawerSf({})}>
          + New Storefront
        </button>
      </div>

      {loading ? (
        <div className="loading-block"><span className="spinner spinner-dark" /></div>
      ) : storefronts.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🏪</div>
          <p>No storefronts yet. Create one to get started.</p>
        </div>
      ) : (
        <div className="sf-admin-grid">
          {storefronts.map(sf => (
            <div key={sf.id} className="sf-admin-card">
              <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:8}}>
                <h2 className="sf-admin-title">{sf.title}</h2>
                <span className={`state-badge ${sf.publish_state}`}>
                  {sf.publish_state === "published" ? "✓ Live" : "● Draft"}
                </span>
              </div>

              <p className="sf-admin-slug">/s/{sf.slug}</p>

              {sf.category && (
                <p style={{fontSize:".78rem",color:"var(--muted)"}}>📦 {sf.category}</p>
              )}

              {sf.publish_state === "published" && sf.published_at && (
                <p style={{fontSize:".75rem",color:"var(--success)"}}>
                  Published {new Date(sf.published_at).toLocaleDateString("en-GB")}
                </p>
              )}

              {previewProds[sf.id] != null && (
                <p style={{fontSize:".78rem",color:"var(--forest)",fontWeight:600}}>
                  📦 {previewProds[sf.id]} products in cache
                </p>
              )}

              <div className="sf-admin-actions">
                {/* Edit */}
                <button className="btn btn-ghost btn-sm" onClick={() => setDrawerSf(sf)}>
                  ✏ Edit
                </button>

                {/* Preview / Refresh cache */}
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => handlePreview(sf)}
                  disabled={busy[sf.id + "_preview"]}
                  title="Fetch products from PA-API (busts cache)"
                >
                  {busy[sf.id + "_preview"] ? <span className="spinner spinner-dark" /> : "🔄 Refresh"}
                </button>

                {/* View live */}
                {sf.publish_state === "published" && (
                  <Link to={`/s/${sf.slug}`} className="btn btn-ghost btn-sm" target="_blank">
                    👁 View
                  </Link>
                )}

                {/* Publish / Unpublish */}
                <button
                  className={`btn btn-sm ${sf.publish_state === "published" ? "btn-ghost" : "btn-forest"}`}
                  onClick={() => handlePublish(sf)}
                  disabled={busy[sf.id]}
                >
                  {busy[sf.id]
                    ? <span className="spinner" />
                    : sf.publish_state === "published" ? "Unpublish" : "🚀 Publish"
                  }
                </button>

                {/* Delete */}
                <button className="btn btn-danger btn-sm" onClick={() => handleDelete(sf)}
                  disabled={busy[sf.id]}>
                  🗑
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {drawerSf !== null && (
        <StorefrontDrawer
          storefront={drawerSf.id ? drawerSf : null}
          onSave={handleSave}
          onClose={() => setDrawerSf(null)}
        />
      )}
    </div>
  );
}
